import { DEFAULT_ROUTE, RESET_PASSWORD_PATH } from 'data/auth/utils'
import VisibilityIcon from 'mdi-react/VisibilityIcon'
import VisibilityOffIcon from 'mdi-react/VisibilityOffIcon'
import React from 'react'
import { Link, Redirect } from 'react-router-dom'

import {
  Button,
  CircularProgress,
  createStyles,
  FormControl,
  IconButton,
  Input,
  InputAdornment,
  InputLabel,
  Theme,
  Typography,
} from '@mui/material'
import { withStyles, WithStyles } from '@mui/styles'

class EmailLoginForm extends React.Component<EmailLoginFormProps, EmailLoginFormState> {
  readonly state = {
    emailValue: '',
    passwordValue: '',
    showPassword: false,
  }

  handleInputChange = (inputName: keyof EmailLoginFormState) => (event: React.ChangeEvent<HTMLInputElement>) => {
    this.setState({
      [inputName as any]: event.target.value,
    } as EmailLoginFormState)
  }

  handleMouseDownPassword = (event: React.MouseEvent) => {
    event.preventDefault()
  }

  handleClickShowPassword = () => {
    this.setState({ showPassword: !this.state.showPassword })
  }

  handleSubmit = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault()
    const { emailValue, passwordValue } = this.state
    this.props.onSubmit({ email: emailValue, password: passwordValue })
  }

  render() {
    const { classes, userLoggedIn, isLoading, authError } = this.props
    const { emailValue, passwordValue, showPassword } = this.state
    if (userLoggedIn) {
      // TODO: make this route dynamic.
      return <Redirect to={DEFAULT_ROUTE} />
    }
    return (
      <>
        <form onSubmit={this.handleSubmit}>
          <FormControl fullWidth className={classes.formControl}>
            <InputLabel htmlFor="email">Email</InputLabel>
            <Input
              id="email"
              type="email"
              value={emailValue}
              color="secondary"
              disabled={isLoading}
              onChange={this.handleInputChange('emailValue')}
              autoFocus={!isLoading}
            />
          </FormControl>
          <FormControl fullWidth className={classes.formControl}>
            <InputLabel htmlFor="password">Password</InputLabel>
            <Input
              id="password"
              type={showPassword ? 'text' : 'password'}
              value={passwordValue}
              color="secondary"
              disabled={isLoading}
              onChange={this.handleInputChange('passwordValue')}
              endAdornment={
                <InputAdornment position="end">
                  <IconButton
                    aria-label="Toggle password visibility"
                    onClick={this.handleClickShowPassword}
                    onMouseDown={this.handleMouseDownPassword}
                  >
                    {showPassword ? (
                      <VisibilityIcon className={classes.activeIcon} />
                    ) : (
                      <VisibilityOffIcon className={classes.icon} />
                    )}
                  </IconButton>
                </InputAdornment>
              }
            />
          </FormControl>
          {authError && (
            <Typography className={classes.errorMessage} variant="caption" color="error">
              The username and password you entered did not match our records. Please double-check and try again.
            </Typography>
          )}
          <Typography variant="body1">
            <Link className={classes.forgottenPassword} to={RESET_PASSWORD_PATH}>
              Forgot password?
            </Link>
          </Typography>
          <div className={classes.submitContainer}>
            <Button color="secondary" size="large" type="submit" disabled={isLoading || !emailValue || !passwordValue}>
              Get started
            </Button>
            {isLoading && <CircularProgress size={24} className={classes.submitProgress} />}
          </div>
        </form>
      </>
    )
  }
}

export type EmailLoginFormProps = {
  userLoggedIn: boolean
  isLoading: boolean
  authError: boolean
  onSubmit: (data: { email: string; password: string }) => any
} & WithStyles<typeof styles>

type EmailLoginFormState = {
  emailValue: string
  passwordValue: string
  showPassword: boolean
}

const styles = ({ palette, spacing, transitions }: Theme) =>
  createStyles({
    formControl: {
      marginBottom: spacing(2),
    },
    submitContainer: {
      position: 'relative',
      display: 'flex',
      justifyContent: 'center',
      marginTop: spacing(3),
      marginBottom: spacing(),
    },
    submitProgress: {
      color: palette.secondary.main,
      position: 'absolute',
      top: '50%',
      left: '50%',
      marginTop: -12,
      marginLeft: -12,
    },
    forgottenPassword: {
      textDecoration: 'none',
      color: palette.grey[400],
      transition: `all .3s ${transitions.easing.easeInOut}`,
      ['&:hover']: {
        color: palette.grey[600],
      },
    },
    errorMessage: {
      marginBottom: spacing(),
    },
    icon: {
      fill: palette.text.secondary,
    },
    activeIcon: {
      fill: palette.primary.main,
    },
  })

export default withStyles(styles)(EmailLoginForm)
